import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import PageLayout from '../components/PageLayout';
import './ApiExplorerPage.css';

interface TryParam {
  name: string;
  in: 'path' | 'query';
  default: string;
  description: string;
  required: boolean;
}

interface CatalogField {
  path: string;
  type: string;
  coverage: number;
  example: unknown;
  usage: 'stored' | 'parsed' | 'unused' | 'n/a';
  note: string;
  tags: string[];
}

interface CatalogEndpoint {
  id: string;
  provider: string;
  method: string;
  path: string;
  summary: string;
  status: 'in-use' | 'available' | 'unavailable';
  used_by: string;
  root: string;
  description: string;
  params: [string, string, string][];
  try: TryParam[] | null;
  http_status: number;
  request_url: string;
  fetched_at: string;
  records: number | null;
  fields: CatalogField[];
  envelope: string[];
  images: string[];
  example: unknown;
}

interface CatalogProvider {
  id: string;
  name: string;
  short: string;
  base: string;
  auth: string;
  docs: string;
  code: string;
  custom_path: string | null;
}

interface Catalog {
  fetched_at: string;
  overview_html: string;
  providers: CatalogProvider[];
  endpoints: CatalogEndpoint[];
}

interface CallResponse {
  status: number;
  duration_ms: number;
  request_url: string;
  headers?: Record<string, string>;
  size_bytes: number;
  truncated: boolean;
  body?: unknown;
  body_text?: string;
  upstream_error?: string;
}

const FILTERS: [string, string][] = [
  ['all', 'All fields'],
  ['stored', 'Stored by us'],
  ['notstored', 'Not stored'],
  ['image', 'Images'],
  ['video', 'Video'],
  ['audio', 'Audio'],
  ['text', 'Long text'],
  ['link', 'Links / social'],
  ['id-xref', 'Cross-ref IDs'],
  ['price', 'Prices'],
  ['genre', 'Genres'],
];

const USAGE_LABEL: Record<string, string> = {
  stored: 'stored',
  parsed: 'read, not stored',
  unused: 'not used',
  'n/a': 'not called',
};

const STATUS_LABEL: Record<string, string> = {
  'in-use': 'IN USE',
  available: 'AVAILABLE · NOT CALLED',
  unavailable: 'BLOCKED',
};

const MAX_HIGHLIGHT_CHARS = 400000;
const IMAGE_URL_RE = /^https?:\/\/\S+(\.(jpe?g|png|webp|gif)(\?\S*)?$|i\.scdn\.co\/image\/|prod-images\.seetickets\.us\/)/i;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

function highlightJson(json: string): string {
  const escaped = json.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  return escaped.replace(
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g,
    (m, str: string | undefined, colon: string | undefined, bool: string | undefined) => {
      if (str) {
        if (colon) return `<span class="k">${str}</span>${colon}`;
        const raw = str.slice(1, -1);
        if (/^https?:\/\/[^"\s]+$/.test(raw)) {
          return `<span class="s">"<a href="${raw.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${raw}</a>"</span>`;
        }
        return `<span class="s">${str}</span>`;
      }
      if (bool) return `<span class="b">${m}</span>`;
      if (m === 'null') return '<span class="z">null</span>';
      return `<span class="n">${m}</span>`;
    }
  );
}

function resolveDefault(value: string): string {
  const now = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 19) + 'Z';
  return value.replace(/\{\{now(?:\+(\d+)d)?\}\}/g, (_m, days?: string) => {
    const d = new Date(now.getTime() + (days ? parseInt(days, 10) : 0) * 86400000);
    return iso(d);
  });
}

function collectImages(value: unknown, out: string[] = [], seen: Record<string, boolean> = {}): string[] {
  if (out.length >= 16) return out;
  if (typeof value === 'string') {
    if (IMAGE_URL_RE.test(value)) {
      const stem = value.replace(/_[A-Z0-9_]+(\.\w+)?$/, '').replace(/performer_image_\d+w/, 'performer_image');
      if (!seen[stem]) {
        seen[stem] = true;
        out.push(value);
      }
    }
  } else if (Array.isArray(value)) {
    for (let i = 0; i < value.length && out.length < 16; i++) collectImages(value[i], out, seen);
  } else if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    Object.keys(obj).forEach((k) => collectImages(obj[k], out, seen));
  }
  return out;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fieldMatches(f: CatalogField, filter: string, query: string): boolean {
  if (filter === 'stored' && f.usage !== 'stored') return false;
  if (filter === 'notstored' && f.usage === 'stored') return false;
  if (filter !== 'all' && filter !== 'stored' && filter !== 'notstored' && f.tags.indexOf(filter) === -1) return false;
  if (query) {
    const ex = typeof f.example === 'string' ? f.example : JSON.stringify(f.example);
    const hay = `${f.path} ${f.note} ${f.tags.join(' ')} ${ex}`.toLowerCase();
    return query.split(/\s+/).every((q) => hay.indexOf(q) !== -1);
  }
  return true;
}

const JsonView: React.FC<{ value: unknown; text?: string }> = ({ value, text }) => {
  const [showAll, setShowAll] = useState(false);
  const pretty = useMemo(() => (text !== undefined ? text : JSON.stringify(value, null, 2)), [value, text]);
  const tooBig = pretty.length > MAX_HIGHLIGHT_CHARS;
  const html = useMemo(() => {
    if (text !== undefined) return escapeHtml(pretty.slice(0, showAll ? undefined : MAX_HIGHLIGHT_CHARS));
    if (tooBig && !showAll) return highlightJson(pretty.slice(0, MAX_HIGHLIGHT_CHARS)) + '\n…';
    return tooBig ? escapeHtml(pretty) : highlightJson(pretty);
  }, [pretty, text, tooBig, showAll]);

  const download = () => {
    const blob = new Blob([pretty], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'response.json';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="apx-toolbar">
        <button type="button" className="apx-btn" onClick={() => navigator.clipboard.writeText(pretty)}>
          Copy
        </button>
        <button type="button" className="apx-btn" onClick={download}>
          Download
        </button>
        {tooBig && (
          <button type="button" className="apx-btn" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show highlighted preview' : `Show all ${formatBytes(pretty.length)} (plain)`}
          </button>
        )}
      </div>
      <pre className="apx-json" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

const Gallery: React.FC<{ images: string[]; label: string }> = ({ images, label }) =>
  images.length ? (
    <div>
      <div className="apx-muted apx-small">{label}</div>
      <div className="apx-gallery">
        {images.map((u) => (
          <a key={u} href={u} target="_blank" rel="noopener noreferrer" title={u}>
            <img loading="lazy" src={u} alt="" />
          </a>
        ))}
      </div>
    </div>
  ) : null;

const ResponseView: React.FC<{ result: CallResponse }> = ({ result }) => {
  const images = useMemo(() => (result.body !== undefined ? collectImages(result.body) : []), [result.body]);
  const statusClass = result.status >= 200 && result.status < 300 ? 'ok' : 'err';
  return (
    <div className="apx-response">
      <div className="apx-response-meta">
        {result.status ? (
          <span className={`apx-http ${statusClass}`}>HTTP {result.status}</span>
        ) : (
          <span className="apx-http err">No response</span>
        )}
        <span>{result.duration_ms} ms</span>
        <span>{formatBytes(result.size_bytes)}</span>
        {result.truncated && <span className="apx-warn">truncated</span>}
      </div>
      <div className="apx-kv">
        <b>Request URL</b>
        <span className="apx-mono apx-break">GET {result.request_url}</span>
        {result.headers && Object.keys(result.headers).length > 0 && (
          <>
            <b>Headers</b>
            <span className="apx-mono apx-small">
              {Object.keys(result.headers).map((k) => (
                <div key={k}>
                  {k}: {result.headers![k]}
                </div>
              ))}
            </span>
          </>
        )}
      </div>
      {result.upstream_error && <div className="apx-error">{result.upstream_error}</div>}
      <Gallery images={images} label="Images in this response" />
      {result.body !== undefined && <JsonView value={result.body} />}
      {result.body_text !== undefined && <JsonView value={null} text={result.body_text} />}
    </div>
  );
};

interface TryItOutProps {
  operation: string;
  params: TryParam[];
  provider?: string;
  customPath?: string;
}

const TryItOut: React.FC<TryItOutProps> = ({ operation, params, provider, customPath }) => {
  const { token } = useAuth();
  const initial = useMemo(() => {
    const v: Record<string, string> = {};
    params.forEach((p) => {
      v[`${p.in}:${p.name}`] = resolveDefault(p.default || '');
    });
    return v;
  }, [params]);
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [path, setPath] = useState(customPath || '/');
  const [extra, setExtra] = useState<{ key: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CallResponse | null>(null);

  const execute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const pathParams: Record<string, string> = {};
    const query: Record<string, string> = {};
    params.forEach((p) => {
      const v = (values[`${p.in}:${p.name}`] || '').trim();
      if (p.in === 'path') pathParams[p.name] = v;
      else if (v) query[p.name] = v;
    });
    extra.forEach(({ key, value }) => {
      if (key.trim() && value.trim()) query[key.trim()] = value.trim();
    });
    setLoading(true);
    setError('');
    try {
      const res = await apiRequest<CallResponse>('/admin/api-explorer/call', token, {
        method: 'POST',
        body: JSON.stringify({
          operation,
          provider,
          path: operation === 'custom' ? path : undefined,
          path_params: pathParams,
          query,
        }),
      });
      setResult(res);
    } catch (err: unknown) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setValues(initial);
    setExtra([]);
    setPath(customPath || '/');
    setResult(null);
    setError('');
  };

  return (
    <form className="apx-try" onSubmit={execute}>
      <table className="apx-try-params">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Value</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {operation === 'custom' && (
            <tr>
              <td className="apx-mono">
                path <span className="apx-in">path</span>
                <span className="apx-req">*</span>
              </td>
              <td>
                <input value={path} onChange={(e) => setPath(e.target.value)} spellCheck={false} />
              </td>
              <td className="apx-muted">Appended to the provider base URL. GET only.</td>
            </tr>
          )}
          {params.map((p) => {
            const key = `${p.in}:${p.name}`;
            return (
              <tr key={key}>
                <td className="apx-mono">
                  {p.name} <span className="apx-in">{p.in}</span>
                  {p.required && <span className="apx-req">*</span>}
                </td>
                <td>
                  <input
                    value={values[key] || ''}
                    onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                    spellCheck={false}
                  />
                </td>
                <td className="apx-muted">{p.description}</td>
              </tr>
            );
          })}
          {extra.map((row, i) => (
            <tr key={`extra-${i}`}>
              <td>
                <input
                  placeholder="name"
                  value={row.key}
                  onChange={(e) => setExtra(extra.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)))}
                  spellCheck={false}
                />
              </td>
              <td>
                <input
                  placeholder="value"
                  value={row.value}
                  onChange={(e) => setExtra(extra.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))}
                  spellCheck={false}
                />
              </td>
              <td>
                <button type="button" className="apx-btn" onClick={() => setExtra(extra.filter((_, j) => j !== i))}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="apx-toolbar">
        <button type="submit" className="apx-execute" disabled={loading}>
          {loading ? 'Calling…' : 'Execute'}
        </button>
        <button type="button" className="apx-btn" onClick={() => setExtra([...extra, { key: '', value: '' }])}>
          + Query param
        </button>
        <button type="button" className="apx-btn" onClick={reset}>
          Reset
        </button>
        <span className="apx-muted apx-small">
          Proxied through the admin API; credentials are added server-side. Ticketmaster shares a 5,000 calls/day quota
          with ingestion.
        </span>
      </div>
      {error && <div className="apx-error">{error}</div>}
      {result && <ResponseView result={result} />}
    </form>
  );
};

interface EndpointCardProps {
  endpoint: CatalogEndpoint;
  filter: string;
  query: string;
  filtering: boolean;
}

const EndpointCard: React.FC<EndpointCardProps> = ({ endpoint: e, filter, query, filtering }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'try' | 'fields' | 'example'>(e.try ? 'try' : 'fields');
  const visibleFields = useMemo(() => e.fields.filter((f) => fieldMatches(f, filter, query)), [e.fields, filter, query]);

  useEffect(() => {
    if (filtering && visibleFields.length) {
      setOpen(true);
      setTab('fields');
    }
  }, [filtering, visibleFields.length]);

  if (filtering && !visibleFields.length) return null;

  return (
    <div className={`apx-op ${e.method === 'CLI' ? 'cli' : ''} st-${e.status} ${open ? 'open' : ''}`} id={e.id}>
      <div className="apx-sum" onClick={() => setOpen(!open)}>
        <span className="apx-badge">{e.method}</span>
        <span className="apx-path">{e.path}</span>
        <span className="apx-ssum">{e.summary}</span>
        <span className={`apx-status ${e.status}`}>{STATUS_LABEL[e.status]}</span>
      </div>
      {open && (
        <div className="apx-body">
          <p className="apx-desc" dangerouslySetInnerHTML={{ __html: e.description }} />
          <div className="apx-kv">
            <b>Used by</b>
            <span dangerouslySetInnerHTML={{ __html: e.used_by }} />
            <b>Sample request</b>
            <span className="apx-mono apx-small apx-break">{e.request_url}</span>
            <b>Sample result</b>
            <span>
              HTTP {e.http_status}
              {e.records !== null ? ` · ${e.records} records` : ''} · fetched {e.fetched_at}
            </span>
            {e.root && (
              <>
                <b>Record path</b>
                <span className="apx-mono">{e.root}</span>
              </>
            )}
          </div>
          <Gallery images={e.images} label="Image URLs found in the saved sample" />
          <div className="apx-tabs">
            {e.try && (
              <span className={`apx-tab ${tab === 'try' ? 'on' : ''}`} onClick={() => setTab('try')}>
                Try it out
              </span>
            )}
            <span className={`apx-tab ${tab === 'fields' ? 'on' : ''}`} onClick={() => setTab('fields')}>
              Fields ({filtering ? `${visibleFields.length} of ${e.fields.length}` : e.fields.length})
            </span>
            <span className={`apx-tab ${tab === 'example' ? 'on' : ''}`} onClick={() => setTab('example')}>
              Saved example
            </span>
          </div>
          <div className="apx-pane">
            {tab === 'try' && e.try && <TryItOut operation={e.id} params={e.try} />}
            {tab === 'fields' && (
              <>
                <div className="apx-muted apx-small apx-envelope">
                  {e.records !== null && (
                    <>
                      Paths are relative to <span className="apx-mono">{e.root}</span>; coverage = % of the {e.records}{' '}
                      sample records where the field is non-empty.{' '}
                    </>
                  )}
                  {e.envelope.length > 0 && (
                    <>
                      Envelope / pagination: <span className="apx-mono">{e.envelope.join(', ')}</span>
                    </>
                  )}
                </div>
                <table className="apx-fields">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Coverage</th>
                      <th>Example value</th>
                      <th>StageSpotter</th>
                      <th>Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFields.map((f) => {
                      const u = f.usage === 'n/a' ? 'n-a' : f.usage;
                      const ex = f.example;
                      return (
                        <tr key={f.path}>
                          <td className="apx-mono apx-break apx-fpath">{f.path}</td>
                          <td className="apx-ftype">{f.type}</td>
                          <td className="apx-fcov">
                            <span className="apx-bar">
                              <i style={{ width: `${f.coverage}%` }} />
                            </span>
                            {f.coverage}%
                          </td>
                          <td className="apx-mono apx-fex">
                            {typeof ex === 'string' && /^https?:\/\//.test(ex) ? (
                              <a href={ex} target="_blank" rel="noopener noreferrer">
                                {ex}
                              </a>
                            ) : ex === null || ex === undefined ? (
                              <span className="apx-muted">null</span>
                            ) : (
                              JSON.stringify(ex)
                            )}
                          </td>
                          <td>
                            <span className={`apx-u ${u}`}>{USAGE_LABEL[f.usage]}</span>
                            {f.note && <span className="apx-note">{f.note}</span>}
                          </td>
                          <td>
                            {f.tags.map((t) => (
                              <span key={t} className={`apx-tag ${t}`}>
                                {t}
                              </span>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
            {tab === 'example' && (
              <>
                <div className="apx-muted apx-small">
                  Response saved on {e.fetched_at}. Arrays trimmed to 2 items, richest records first.
                </div>
                <JsonView value={e.example} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomRequestCard: React.FC<{ provider: CatalogProvider }> = ({ provider }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`apx-op custom ${open ? 'open' : ''}`}>
      <div className="apx-sum" onClick={() => setOpen(!open)}>
        <span className="apx-badge">GET</span>
        <span className="apx-path">{'{any path}'}</span>
        <span className="apx-ssum">Custom request to {provider.short}</span>
        <span className="apx-status available">ADVANCED</span>
      </div>
      {open && (
        <div className="apx-body">
          <p className="apx-desc">
            Call any GET path under <code>{provider.base}</code> with our credentials, for endpoints not listed above.
          </p>
          <TryItOut operation="custom" provider={provider.id} customPath={provider.custom_path || '/'} params={[]} />
        </div>
      )}
    </div>
  );
};

const ApiExplorerPage: React.FC = () => {
  const { token } = useAuth();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [showOverview, setShowOverview] = useState(true);

  useEffect(() => {
    if (!token || catalog) return;
    apiRequest<Catalog>('/admin/api-explorer/catalog', token)
      .then(setCatalog)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load catalog'));
  }, [token, catalog]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const filtering = filter !== 'all' || query !== '';
  const matchCount = useMemo(() => {
    if (!catalog || !filtering) return 0;
    return catalog.endpoints.reduce((n, e) => n + e.fields.filter((f) => fieldMatches(f, filter, query)).length, 0);
  }, [catalog, filter, query, filtering]);

  return (
    <PageLayout title="API Explorer">
      <div className="apx">
        {error && <div className="apx-error">{error}</div>}
        {!catalog && !error && <div className="apx-muted">Loading catalog…</div>}
        {catalog && (
          <>
            <div className="apx-intro">
              Everything our vendor APIs return, with field coverage from saved samples (fetched{' '}
              {catalog.fetched_at.slice(0, 16).replace('T', ' ')} UTC) and live <b>Try it out</b> calls.
            </div>
            <div className="apx-searchbar">
              <input
                type="search"
                placeholder="Search fields, e.g. image, youtube, price, musicbrainz…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="apx-jump">
                {catalog.providers.map((p) => (
                  <a key={p.id} href={`#p-${p.id}`}>
                    {p.short}
                  </a>
                ))}
              </div>
            </div>
            <div className="apx-filters">
              {FILTERS.map(([k, label]) => (
                <span key={k} className={`apx-chip ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)}>
                  {label}
                </span>
              ))}
              {filtering && <span className="apx-count">{matchCount} matching fields</span>}
            </div>

            {!filtering && (
              <div className="apx-overview-wrap">
                <button type="button" className="apx-btn" onClick={() => setShowOverview(!showOverview)}>
                  {showOverview ? 'Hide' : 'Show'} quick answers &amp; capability matrix
                </button>
                {showOverview && (
                  <div className="apx-overview" dangerouslySetInnerHTML={{ __html: catalog.overview_html }} />
                )}
              </div>
            )}

            {catalog.providers.map((p) => {
              const eps = catalog.endpoints.filter((e) => e.provider === p.id);
              const anyVisible =
                !filtering || eps.some((e) => e.fields.some((f) => fieldMatches(f, filter, query)));
              if (!anyVisible) return null;
              return (
                <section className="apx-provider" id={`p-${p.id}`} key={p.id}>
                  <h3>{p.name}</h3>
                  <div className="apx-pmeta">
                    Base: <code>{p.base}</code> · Auth: <span dangerouslySetInnerHTML={{ __html: p.auth }} />
                    {p.docs && (
                      <>
                        {' '}
                        ·{' '}
                        <a href={p.docs} target="_blank" rel="noopener noreferrer">
                          Vendor docs
                        </a>
                      </>
                    )}{' '}
                    · Code: <code>{p.code}</code>
                  </div>
                  {eps.map((e) => (
                    <EndpointCard key={e.id} endpoint={e} filter={filter} query={query} filtering={filtering} />
                  ))}
                  {!filtering && p.custom_path && <CustomRequestCard provider={p} />}
                </section>
              );
            })}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default ApiExplorerPage;
